import { Component, EventEmitter, Input, OnInit, Output, ViewChild, SimpleChanges } from '@angular/core';
import { BaseDynamicFormComponent } from 'src/app/common/components/dynamic-form-v2/components/base-dynamic-form/base-dynamic-form.component';
import { DynamicFormV2Component } from 'src/app/common/components/dynamic-form-v2/dynamic-form-v2.component';
import { DynamicCardType } from 'src/app/common/components/dynamic-form-v2/enums/DynamicCardType';
import { DynamicInputType } from 'src/app/common/components/dynamic-form-v2/enums/DynamicInputType';
import RewardsTrackDTO from '../../dtos/RewardsTrackDTO';
import DefaultTracks from '../../../season-pass/dtos/defaultTracks';
import { InputFieldType } from 'src/app/common/components/dynamic-form-v2/enums/InputFieldType';
import { DataService } from 'src/app/services/data.service';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';

enum RewardsTrackType {
  DAILY_1_7 = 1,
  DAILY_30 = 2,
  SEASON_PASS_FREE = 3,
  SEASON_PASS_PAID = 4,
  FRIENDS = 5,
  EO_TRACK = 6,
  COOP = 7,
  MINI_GAMES = 8,
  SEASONAL_PRIZES = 9,
  DAILY_ACTIVITIES = 10,
  PERK_DAILY_REWARD = 11,
  GIFTING_TREE_V2 = 12
}

@Component({
  selector: 'rewards-track-form-v3',
  templateUrl: './rewards-track-form-v3.component.html',
  styleUrls: ['./rewards-track-form-v3.component.sass']
})
export class RewardsTrackFormV3Component extends BaseDynamicFormComponent implements OnInit
{
  @ViewChild(DynamicFormV2Component) dynamicFormComponent: DynamicFormV2Component;
  @Input() rewardsTrackRecord: RewardsTrackDTO = new RewardsTrackDTO();
  @Input() isChildComponent: boolean = false;
  @Input() disableTypeField: boolean = false;
  @Output() onRewardsTrackRecordChange = new EventEmitter<any>();
  defaultTracks: DefaultTracks = new DefaultTracks();
  templateTracks: any[] = [];
  private originalType: number | null = null; // Store original type number when mapping to _id
  private templatesLoaded: boolean = false; // Track if templates have been loaded

  // Field mapping for each track type
  private readonly trackTypeFields: { [key in RewardsTrackType]?: string[] } = {
    [RewardsTrackType.DAILY_1_7]: ['dailyRewardDay1', 'dailyRewardDay2', 'dailyRewardDay3', 'dailyRewardDay4', 'dailyRewardDay5', 'dailyRewardDay6', 'dailyRewardDay7', 'stageMilestone'],
    [RewardsTrackType.DAILY_30]: ['dailyRewardDay30', 'stageMilestone'],
    [RewardsTrackType.SEASON_PASS_FREE]: ['free', 'stageMilestone'],
    [RewardsTrackType.SEASON_PASS_PAID]: ['paid', 'stageMilestone'],
    [RewardsTrackType.FRIENDS]: ['friends'],
    [RewardsTrackType.EO_TRACK]: ['EOrewards', 'EObundles'],
    [RewardsTrackType.COOP]: ['coop', 'stageMilestone'],
    [RewardsTrackType.MINI_GAMES]: ['miniGamesRewards'],
    [RewardsTrackType.SEASONAL_PRIZES]: ['seasonalPrizes'],
    [RewardsTrackType.DAILY_ACTIVITIES]: ['dailyActivitiesRewards'],
    [RewardsTrackType.PERK_DAILY_REWARD]: ['perkDailyReward'],
    [RewardsTrackType.GIFTING_TREE_V2]: ['giftingTreeV2Rewards', 'stageMilestone']
  };

  // Helper function to get fields to copy based on track type
  private getFieldsToCopy(trackType: RewardsTrackType): string[] {
    return this.trackTypeFields[trackType] || [];
  }

  // Helper function to extract relevant fields from template
  private extractRelevantFields(template: any, trackType: RewardsTrackType): { [key: string]: any } {
    const relevantFields = this.getFieldsToCopy(trackType);
    const extractedFields: { [key: string]: any } = {};
    
    relevantFields.forEach(field => {
      if (template[field] !== undefined) {
        extractedFields[field] = template[field];
      }
    });

    return extractedFields;
  }

  constructor(
    private dataService: DataService,
    private commonEntityService: CommonEntityService,
  ) {
    super();
  }

  /**
   * Fetches template reward tracks
   * Note: includeFullRewards is needed to get the complete reward arrays (coop, free, paid, etc.)
   * with their count values, since the default list query excludes these for performance.
   */
  async getTemplateTracks() {
    const templateTracks = await this.dataService.getAllOfTypeAsync('rewards-track', {
      query: { isTemplate: true },
      sort: { createdAt: -1 },
      includeFullRewards: true  // Include all reward fields (coop, stageMilestone, etc.)
    });

    return templateTracks;
  }

  private async getFullTemplateTrack(template: any): Promise<any> {
    if (!template?.id || template.__fullLoaded) {
      if (!template?.id) {
        console.warn('Rewards track template missing id; skipping full load.', template);
      }
      return template;
    }

    const fullTemplate = await this.commonEntityService.findOne('rewards-track/full', template.id);
    if (!fullTemplate) {
      return template;
    }

    return {
      ...template,
      ...fullTemplate,
      __fullLoaded: true
    };
  }

  /**
   * Rewards Track Form V3 Component Initialization
   */
  async ngOnInit()
  {
    // Fetch template tracks
    this.templateTracks = await this.getTemplateTracks();
    this.templatesLoaded = true;
    
    // CRITICAL: Map type to template _id BEFORE defining fields
    // This ensures the form binds to the correct value (template _id) instead of type number
    if (this.rewardsTrackRecord && this.rewardsTrackRecord.type !== undefined && typeof this.rewardsTrackRecord.type === 'number') {
      this.mapTypeToTemplateId();
    }

    this.fields =
    [
      {
        title: "Rewards Track Data",
        fields:
        [
          {
            name: "Name",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.TextInput],
            clearField: false, // Disable clear field to prevent automatic clearing
            key: 'name',
            isRequired: true,
            label: 'Name',
            disabled: false
          },
          {
            name: "Type",
            inputType: DynamicInputType.BaseInputField,
            inputTypeFields: [InputFieldType.Dropdown],
            clearField: true,
            key: 'type',
            label: 'Type',
            filter: true,
            autoDisplayFirst: false,
            optionLabel: 'name',
            optionValue: 'value',
            isRequired: true,
            disabled: this.disableTypeField,
            options:
            {
              fieldName: 'type',
              values: this.templateTracks.map(track => ({
                name: track.name,
                value: track._id || track.id, // Use unique identifier instead of type
                type: track.type // Include type for matching when editing existing records
              }))
            }
          },
          {
            name: "Track",
            inputType: DynamicInputType.RewardsTrackInputComponent,
            key: 'track',
          },
        ],
        type: DynamicCardType.Card
      }
    ]

    this.title = "Rewards Track";
    this.viewRoute = 'rewards-track';
    this.isLoading = false;
  }

  /**
   * Handle changes to input properties
   */
  ngOnChanges(changes: SimpleChanges) {
    // When rewardsTrackRecord changes, map type value to template _id for dropdown display
    if (changes['rewardsTrackRecord'] && changes['rewardsTrackRecord'].currentValue) {
      const record = changes['rewardsTrackRecord'].currentValue;
      
      // Only map if we have templates loaded and record has a numeric type value
      if (this.templatesLoaded && this.templateTracks && this.templateTracks.length > 0 && 
          record?.type !== undefined && typeof record.type === 'number') {
        this.mapTypeToTemplateId();
        // Emit the change so parent components are aware of the conversion
        this.onRewardsTrackRecordChange.emit(this.rewardsTrackRecord);
      }
    }
  }
  
  /**
   * Maps the type value to a template _id for dropdown display when editing
   * This ensures the dropdown can match the value when editing existing records
   * IMPORTANT: This must be called BEFORE fields are defined so the form binds to the correct value
   * 
   * NOTE: We store the original numeric type in a separate property so the track component
   * can still access it, since it needs the number to determine which fields to display
   */
  private mapTypeToTemplateId() {
    if (!this.rewardsTrackRecord || !this.templateTracks.length) {
      return;
    }
    
    const currentType = this.rewardsTrackRecord.type;
    
    // If type is a number, find the first matching template and use its _id for display
    if (typeof currentType === 'number') {
      // Store the original type number
      this.originalType = currentType;
      
      // Store the numeric type in a separate property for the track component
      // The track component needs the numeric type to determine which fields to display
      (this.rewardsTrackRecord as any).__originalType = currentType;
      
      // Find the first template with matching type (for display purposes)
      const matchingTemplate = this.templateTracks.find(track => track.type === currentType);
      
      if (matchingTemplate) {
        const templateId = matchingTemplate._id || matchingTemplate.id;
        if (templateId) {
          // Create a new object reference to ensure change detection works
          // This is critical - we need a new reference, not just mutating the existing one
          this.rewardsTrackRecord = {
            ...this.rewardsTrackRecord,
            type: templateId,
            __originalType: currentType // Preserve numeric type for track component
          };
        }
      }
    } else if (currentType && typeof currentType !== 'number' && this.originalType === null) {
      // If type is already an _id, find the template and store its type number
      const currentTypeStr = String(currentType);
      const matchingTemplate = this.templateTracks.find(track => {
        const trackId = track._id ? String(track._id) : (track.id ? String(track.id) : null);
        return trackId === currentTypeStr;
      });
      if (matchingTemplate) {
        this.originalType = matchingTemplate.type;
        // Store the numeric type for the track component
        (this.rewardsTrackRecord as any).__originalType = matchingTemplate.type;
      }
    }
  }

  /**
   * Emits changes comming from child component
   * to parent component.
   *
   * @param event Envent comming from child component.
   */
  async onChange(event: any)
  { 
    if(event.field == 'type') {
      // Find the selected template track by _id (unique identifier) instead of type
      const eventValueStr = event.value ? String(event.value) : null;
      let selectedTemplate = this.templateTracks.find(track => {
        const trackId = track._id ? String(track._id) : (track.id ? String(track.id) : null);
        return trackId === eventValueStr;
      });

      if (selectedTemplate) {
        selectedTemplate = await this.getFullTemplateTrack(selectedTemplate);
        if (selectedTemplate?.id && !selectedTemplate.__fullLoaded) {
          selectedTemplate.__fullLoaded = true;
        }
        // Update cached template with full data so we don't re-fetch
        if (selectedTemplate?.id) {
          const templateIndex = this.templateTracks.findIndex(track => track.id === selectedTemplate.id);
          if (templateIndex >= 0) {
            this.templateTracks[templateIndex] = selectedTemplate;
          }
        }

        // Save the current values from the event record (which contains the form's current state)
        const currentName = event.record?.name || this.rewardsTrackRecord.name;
        const currentEnv = event.record?.env || this.rewardsTrackRecord.env;
        
        // Extract only the relevant fields based on track type
        const relevantFields = this.extractRelevantFields(selectedTemplate, selectedTemplate.type);
        
        // Store the type number for the track component and for saving
        this.originalType = selectedTemplate.type;
        
        // Keep type as the template _id for dropdown display, but store numeric type separately
        const templateId = selectedTemplate._id || selectedTemplate.id;
        const updatedRecord = {
          ...this.rewardsTrackRecord,
          ...relevantFields, // Apply only the relevant template fields
          type: templateId, // Keep as _id for dropdown display
          __originalType: selectedTemplate.type, // Store numeric type for track component
          isTemplate: this.rewardsTrackRecord.isTemplate !== undefined ? this.rewardsTrackRecord.isTemplate : false, // Preserve existing isTemplate value or default to false for regular tracks
          name: currentName || '', // Preserve the original name
          env: currentEnv, // Preserve the original env value
        };
        
        // Update the record reference to trigger change detection
        this.rewardsTrackRecord = updatedRecord;
      } else {
        // Handle default tracks for Garden Pass types (when no template is selected)
        // Note: event.value is now _id, so we need to check if it's a type number
        const trackType = typeof event.value === 'number' ? event.value : null;
        if (trackType !== null) {
          switch (trackType) {
            case RewardsTrackType.SEASON_PASS_FREE:
              this.rewardsTrackRecord.free = this.defaultTracks.FreeDefaultTrack;
              this.rewardsTrackRecord.stageMilestone = this.defaultTracks.FreeDefaultStageMilestones;
              break;
            case RewardsTrackType.SEASON_PASS_PAID:
              this.rewardsTrackRecord.paid = this.defaultTracks.PaidDefaultTrack;
              this.rewardsTrackRecord.stageMilestone = this.defaultTracks.PaidDefaultStageMilestones;
              break;
          }
        }
      }
    }
    
    // Create a copy of the record with type converted to number for parent component
    // But keep the original record with _id for dropdown display
    const recordToEmit = this.getRecordWithNumericType();
    
    this.onRewardsTrackRecordChange.emit(recordToEmit);
  }
  
  private readonly flatRewardFields = [
    'dailyRewardDay1', 'dailyRewardDay2', 'dailyRewardDay3', 'dailyRewardDay4',
    'dailyRewardDay5', 'dailyRewardDay6', 'dailyRewardDay7', 'dailyRewardDay30',
  ];

  private readonly nestedRewardFields = [
    'free', 'paid', 'friends', 'coop',
    'EOrewards', 'EObundles',
    'miniGamesRewards', 'seasonalPrizes', 'dailyActivitiesRewards',
    'perkDailyReward', 'giftingTreeV2Rewards', 'giftingTreeRewards',
  ];

  /**
   * Transforms the API payload before submission, replacing fully populated
   * line item objects with just their _ids. Operates on the payload copy
   * built by dynamic-form-v2, so the live record is never mutated.
   */
  readonly payloadTransformer = (payload: any): any => {
    for (const field of this.flatRewardFields) {
      if (Array.isArray(payload[field])) {
        payload[field] = payload[field].map((item: any) => ({
          t: item?.t?._id ?? item?.t,
          id: item?.id?._id ?? item?.id,
          c: item?.c,
          lineItemType: item?.lineItemType,
        }));
      }
    }
    for (const field of this.nestedRewardFields) {
      if (Array.isArray(payload[field])) {
        payload[field] = payload[field].map((stage: any) =>
          Array.isArray(stage) ? stage.map((item: any) => ({
            t: item?.t?._id ?? item?.t,
            id: item?.id?._id ?? item?.id,
            c: item?.c,
            lineItemType: item?.lineItemType,
          })) : stage
        );
      }
    }
    return payload;
  };

  /**
   * Last line of defence against a save that silently empties stages which had
   * rewards when the record was loaded.
   *
   * dynamic-form-v2 treats a falsy `confirmValidationCallback` as "ask first",
   * not as a hard rejection, so clearing a stage on purpose (deleting its last
   * reward) stays possible - it just can't happen without the user seeing it.
   * A server-side `IsArrayOfArraysNotEmpty` would have been a blanket ban, and
   * blank stages are legitimate here: addStage() creates one on purpose and the
   * user fills it afterwards.
   */
  readonly validateTrackRewards = (record: any): { confirmValidationCallback: boolean; message: string } => {
    const original = this.dynamicFormComponent?.originalRecord;
    if (!original || !record) {
      return { confirmValidationCallback: true, message: 'Valid' };
    }

    const blanked: string[] = [];

    for (const field of this.flatRewardFields) {
      const before = original[field];
      const after = record[field];
      if (Array.isArray(before) && before.length > 0 && Array.isArray(after) && after.length === 0) {
        blanked.push(field);
      }
    }

    for (const field of this.nestedRewardFields) {
      const before = original[field];
      const after = record[field];
      if (!Array.isArray(before) || !Array.isArray(after)) {
        continue;
      }
      after.forEach((stage: any, index: number) => {
        const hadRewards = Array.isArray(before[index]) && before[index].length > 0;
        const hasRewards = Array.isArray(stage) && stage.length > 0;
        if (hadRewards && !hasRewards) {
          blanked.push(`${field} stage ${index + 1}`);
        }
      });
    }

    if (blanked.length === 0) {
      return { confirmValidationCallback: true, message: 'Valid' };
    }

    return {
      confirmValidationCallback: false,
      message: `This save removes every reward from: ${blanked.join(', ')}.`,
    };
  };

  /**
   * Intercepts form submission to ensure type is converted to number before saving.
   * This is called by the dynamic form component before it submits.
   */
  onSubmitAction(record: any) {
    const numericType = this.getNumericTypeFromRecord(record);
    if (numericType !== null && typeof record.type !== 'number') {
      record.type = numericType;
      this.rewardsTrackRecord.type = numericType;
    }
  }
  
  /**
   * Gets the numeric type from a record (helper for onSubmitAction)
   */
  private getNumericTypeFromRecord(record: any): number | null {
    if (!record) {
      return null;
    }
    
    const currentType = record.type;
    
    // If type is already a number, return it
    if (typeof currentType === 'number') {
      return currentType;
    } 
    // If we have stored originalType, use it
    else if (this.originalType !== null) {
      return this.originalType;
    }
    // Otherwise, try to find the template and get its type
    else {
      const currentTypeStr = String(currentType);
      const matchingTemplate = this.templateTracks.find(track => {
        const trackId = track._id ? String(track._id) : (track.id ? String(track.id) : null);
        return trackId === currentTypeStr;
      });
      return matchingTemplate ? matchingTemplate.type : null;
    }
  }
  
  /**
   * Returns a copy of the record with type as a number (for saving)
   * while keeping the original record with _id for dropdown display
   */
  private getRecordWithNumericType(): any {
    if (!this.rewardsTrackRecord) {
      return this.rewardsTrackRecord;
    }
    
    const currentType = this.rewardsTrackRecord.type;
    let numericType: number | null;
    
    // If type is already a number, use it
    if (typeof currentType === 'number') {
      numericType = currentType;
    } 
    // If we have stored originalType, use it
    else if (this.originalType !== null) {
      numericType = this.originalType;
    }
    // Otherwise, try to find the template and get its type
    else {
      const currentTypeStr = String(currentType);
      const matchingTemplate = this.templateTracks.find(track => {
        const trackId = track._id ? String(track._id) : (track.id ? String(track.id) : null);
        return trackId === currentTypeStr;
      });
      const fallback = this.originalType 
      numericType = matchingTemplate?.type ?? fallback 
    }
    
    // Return a copy with numeric type, but don't modify the original record
    return {
      ...this.rewardsTrackRecord,
      type: numericType
    };
  }
  
  /**
   * Ensures the type field is a number (not an _id) before saving/emitting
   */
  private ensureTypeIsNumber() {
    if (!this.rewardsTrackRecord) {
      return;
    }
    
    const currentType = this.rewardsTrackRecord.type;
    
    // If type is not a number, try to restore it
    if (typeof currentType !== 'number') {
      // If we have a stored originalType, use it
      if (this.originalType !== null) {
        this.rewardsTrackRecord.type = this.originalType;
      } else {
        // Otherwise, try to find the template and get its type
        const currentTypeStr = String(currentType);
        const matchingTemplate = this.templateTracks.find(track => {
          const trackId = track._id ? String(track._id) : (track.id ? String(track.id) : null);
          return trackId === currentTypeStr;
        });
        if (matchingTemplate) {
          this.rewardsTrackRecord.type = matchingTemplate.type;
          this.originalType = matchingTemplate.type;
        }
      }
    } else {
      // If it's already a number, store it as originalType
      this.originalType = currentType;
    }
  }
  
  /**
   * Converts type to number from any record (used for form submission)
   */
  private getRecordWithNumericTypeFromRecord(record: any): any {
    if (!record) {
      return record;
    }
    
    const currentType = record.type;
    let numericType: number | null;
    
    // If type is already a number, use it
    if (typeof currentType === 'number') {
      numericType = currentType;
    } 
    // If we have stored originalType, use it
    else if (this.originalType !== null) {
      numericType = this.originalType;
    }
    // Otherwise, try to find the template and get its type
    else {
      const currentTypeStr = String(currentType);
      const matchingTemplate = this.templateTracks.find(track => {
        const trackId = track._id ? String(track._id) : (track.id ? String(track.id) : null);
        return trackId === currentTypeStr;
      });
      const fallback = this.originalType 
      numericType = matchingTemplate?.type ?? fallback 
    }
    
    // Return a copy with numeric type
    return {
      ...record,
      type: numericType
    };
  }
}
